import Button from '@/components/Button/Button';
import { useVacationRequestForm } from './hooks';
import ModalityFieldset from './components/ModalityFieldset';
import VacationPeriodFields from './components/VacationPeriodFields';
import SellDaysCheckbox from './components/SellDaysCheckbox';
import ContactFields from './components/ContactFields';
import VacationSegmentsSummary from './components/VacationSegmentsSummary';
import RuleIssuesPanel from './components/RuleIssuesPanel';
import { MODALITY_REQUIRED_HINT } from './vacationRequestForm.definitions';
import type { VacationRequestFormProps } from './vacationRequestForm.types';
import type { VacationModality } from '../../models/vacation.types';

export function VacationRequestForm({
    balance,
    isSubmitting,
    onSubmit,
    earliestStartDate,
    scheduleLimitDate,
    holidays,
    managerSuggestions,
    hasMultipleAllocationManagers,
    currentCollaboratorId,
    initialValues,
}: VacationRequestFormProps) {
    const {
        control,
        register,
        getFieldError,
        clientEmails,
        clientEmailsError,
        addClientEmail,
        removeClientEmail,
        submit,
        modality,
        isFractioned,
        isPartialBalance,
        periodDays,
        sellDaysRequested,
        segments,
        totalDays,
        secondEndDate,
        firstEndOptions,
        canSelectFirstEnd,
        canSelectSecondStart,
        firstStartDefaultMonth,
        secondStartDefaultMonth,
        isStartDateSelectable,
        isSecondStartSelectable,
        panelErrors,
        panelWarnings,
        fieldErrors,
        fieldWarnings,
        canSubmit,
        handlers,
    } = useVacationRequestForm({
        balance,
        isSubmitting,
        onSubmit,
        earliestStartDate,
        scheduleLimitDate,
        holidays,
        managerSuggestions,
        initialValues,
    });

    return (
        <form onSubmit={submit} className="space-y-6" noValidate>
            <div data-tour="vacation-form-format">
                <ModalityFieldset
                    value={modality}
                    disabled={isSubmitting}
                    sellDaysRequested={sellDaysRequested}
                    partialBalanceDays={isPartialBalance ? periodDays : undefined}
                    registerProps={register('modality', {
                        onChange: (event) => handlers.onModalityChange(event.target.value as VacationModality),
                    })}
                />
            </div>

            {!modality && (
                <p className="text-sm text-raro-gray-500 font-montserrat">{MODALITY_REQUIRED_HINT}</p>
            )}

            {modality && (
                <>
                    <div data-tour="vacation-form-start-date">
                        <VacationPeriodFields
                            isFractioned={isFractioned}
                            isSubmitting={isSubmitting}
                            control={control}
                            getFieldError={getFieldError}
                            firstEndOptions={firstEndOptions}
                            canSelectFirstEnd={canSelectFirstEnd}
                            canSelectSecondStart={canSelectSecondStart}
                            firstStartDefaultMonth={firstStartDefaultMonth}
                            secondStartDefaultMonth={secondStartDefaultMonth}
                            secondEndDate={secondEndDate}
                            isStartDateSelectable={isStartDateSelectable}
                            isSecondStartSelectable={isSecondStartSelectable}
                            onFirstStartChange={handlers.onFirstStartChange}
                            onFirstEndChange={handlers.onFirstEndChange}
                            onSecondStartChange={handlers.onSecondStartChange}
                            fieldErrors={fieldErrors}
                            fieldWarnings={fieldWarnings}
                        />
                    </div>

                    {!isFractioned && !isPartialBalance && (
                        <div data-tour="vacation-form-selling">
                            <SellDaysCheckbox registerProps={register('sellDaysRequested')} />
                        </div>
                    )}

                    <ContactFields
                        control={control}
                        register={register}
                        getFieldError={getFieldError}
                        isSubmitting={isSubmitting}
                        managerSuggestions={managerSuggestions}
                        hasMultipleAllocationManagers={hasMultipleAllocationManagers}
                        currentCollaboratorId={currentCollaboratorId}
                        clientEmails={clientEmails}
                        clientEmailsError={clientEmailsError}
                        onAddClientEmail={addClientEmail}
                        onRemoveClientEmail={removeClientEmail}
                    />

                    <VacationSegmentsSummary segments={segments} totalDays={totalDays} />
                </>
            )}

            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <RuleIssuesPanel errors={panelErrors} warnings={panelWarnings} />
                </div>
                <Button type="submit" disabled={!canSubmit} loading={isSubmitting} className="mt-0" data-tour="vacation-form-submit">
                    Enviar solicitação
                </Button>
            </div>
        </form>
    );
}

export default VacationRequestForm;
